import alertDialogService, { type ValidationResultsDialogOptions } from "AlertDialogService";
import captionService from "CaptionService";
import { TaskMessageBusEvent } from "Constants";
import type { FormFlowUI } from "FormFlowUITypes";
import { MessageType } from "MessageBus";
import NotificationSummary from "NotificationSummary";
import { NotificationType } from "NotificationType";
import type { Alert } from "Notifications";
import { getPageExtensions } from "PageExtensions";
import validationEngine from "ValidationEngine";
import type { ValidationRegistrarItem } from "ValidationRegistrar";
import ko from "knockout";

export async function validateFormAsync(formFlowUI: FormFlowUI, suspendValidation?: boolean): Promise<boolean> {
  if (suspendValidation) {
    return true;
  }

  if (!(await validateControlsAsync(formFlowUI))) {
    return false;
  }

  const items = await getBoundItemsExcludeDetachedAndDeletedAsync(formFlowUI);
  if (hasClientSideAlert(isControlError, items)) {
    return false;
  }

  if (!formFlowUI.hasChanges()) {
    return true;
  }

  const results = await Promise.all(
    items.map((i) =>
      validationEngine.validateEntityAsync(i.entity, i.propertyNames, [
        NotificationType.Error,
        NotificationType.Warning,
      ]),
    ),
  );

  const isValid = results.every(Boolean);
  if (isValid) {
    items.forEach((item) => {
      item.entity.entityAspect.notifications.removeAll(
        (a: Alert) =>
          a.Level === NotificationType.Warning &&
          !a.requiresAcknowledgement() &&
          item.propertyNames.includes(a.propertyName),
      );
    });
  }

  return isValid;
}

export async function showValidationResultsAsync(
  formFlowUI: FormFlowUI,
  filter: (alert: Alert) => boolean | null,
): Promise<void> {
  const message = captionService.getString("5062bd45-3f0c-49f8-a9f6-08dd31ffe254", "Validation failed, please review");
  const title = captionService.getString("7cd7bac9-11f1-4a09-a4f6-647a18f9c42e", "Validation failed");
  const entities = ko.observable(formFlowUI.getAllEntities());
  const notificationSummary = new NotificationSummary(null, entities);
  const options: ValidationResultsDialogOptions = { filter };

  await alertDialogService.showValidationResultsAsync(message, title, notificationSummary, options);
}

export function isRuleErrorOrWarning(alert: Alert): boolean {
  return (
    !!alert.ruleId &&
    (alert.Level === NotificationType.Error ||
      (alert.Level === NotificationType.Warning && alert.requiresAcknowledgement() && !alert.acknowledged?.()))
  );
}

export function isControlError(alert: Alert): boolean {
  return !alert.ruleId && alert.Level === NotificationType.Error;
}

export function hasClientSideAlert(
  predicate: (alert: Alert) => boolean,
  boundItems?: ValidationRegistrarItem[],
): boolean {
  if (boundItems) {
    return boundItems.some((item) => {
      return item.propertyNames.some((property) => {
        return item.entity.entityAspect.notifications.alerts(property).some((alert: Alert) => {
          return !alert.isServerNotification && predicate(alert);
        });
      });
    });
  }
  return false;
}

export async function validateControlsAsync(formFlowUI: FormFlowUI): Promise<boolean | undefined> {
  const pageExtensions = getPageExtensions(formFlowUI.formBindingContext);
  const validateControlsAsyncMessageType = new MessageType<boolean, boolean>(TaskMessageBusEvent.ValidateControls);
  const result = await pageExtensions.messageBus?.publishAsync(validateControlsAsyncMessageType);
  return result?.every((r) => r === true);
}

export async function getBoundItemsExcludeDetachedAndDeletedAsync(
  formFlowUI: FormFlowUI,
): Promise<ValidationRegistrarItem[]> {
  const pageExtensions = getPageExtensions(formFlowUI.formBindingContext);
  const validationRegistrarItems = await pageExtensions.validationRegistrar?.getBoundItemsAsync();
  if (validationRegistrarItems) {
    const excludeDetachedAndDeletedEntities = (item: ValidationRegistrarItem): boolean =>
      !item.entity.entityAspect.entityState.isDetached() && !item.entity.entityAspect.entityState.isDeleted();
    return validationRegistrarItems.filter(excludeDetachedAndDeletedEntities);
  }
  return [];
}
